import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import {
  inject,
  Injectable, Injector, StaticProvider, Type,
} from '@angular/core';
import { from, Observable } from 'rxjs';
import { skip, tap } from 'rxjs/operators';
import {
  ApActiveModal,
  ApDialogConfig,
  ApModalServiceBase,
  AnyModalComponent,
  InferModalData,
  InferModalResult,
} from 'webcommon/core-ui/modals-ng1520';

import { Dialog } from '@angular/cdk/dialog';
import {
  destroyManualInjector,
  destroyManualInjectorWhen,
  ExactlyPartial,
  NgZoneService,
  stringifyClass,
} from 'webcommon/shared';

import { BrowserService, ScopeProvider } from 'webcommon/legacy-common';
import { CgmChatService } from 'webcommon/common-api';

@Injectable({
  providedIn: 'root',
})
// This is a wrapper around BsModalService to provide more specific
// for our use of the modals.
// Also, it provides typing around stuff that ngx-boostrap doesn't have types for!
export class ApModalService extends ApModalServiceBase {
  private browserService = inject(BrowserService);
  private documentElement = this.browserService.getDocument().documentElement;
  private dialog = inject(Dialog);
  private cgmChatService = inject(CgmChatService);
  // For a root service like this, this is a module Injector, which is actually an instance of R3Injector at runtime.
  // Unless it's provided separately than the 'root' module, then that specific case have a different instance.
  // (https://github.com/angular/angular/blob/main/packages/core/src/di/r3_injector.ts)
  private moduleInjector = inject(Injector);
  private ngZoneService = inject(NgZoneService);
  private overlay = inject(Overlay);
  private overlayContainer = inject(OverlayContainer);


  constructor() {
    super();
    this.dialog.afterAllClosed.pipe(
      // skip 1 because it should emit right after subscribed the first time
      skip(1),
      tap(() => {
        this.makeCompatibleWithBootstrapModalAllClosed({
          overlayContainer: this.overlayContainer,
          documentElement: this.documentElement,
        });
      }),
    ).subscribe();
  }

  // If you see an error for
  // "Types of property 'someProperty' are incompatible. Type 'someType' is not assignable to type 'never'",
  // then it's because 'someProperty' doesn't exist on TComponent.
  // So it's probably a typo, or you haven't yet added that property to 'TComponent'.
  //
  // If you see an error for
  // "Types of property 'someProperty2' are incompatible. Type 'someType2' is not assignable to type 'someOtherType'",
  // then it's because 'someProperty2' probably does exist on 'TComponent', but you're giving it the wrong type.
  //
  // This is essentially making sure that the properties passed on the 'initialState' match 'TComponent',
  // which is most likely necessary for 'TComponent' to function properly.
  // The type that 'TComponentState' extends from has to be an intersection with some variant of a 'Partial<TComponent>'
  // so as to not require the instance methods to be filled in on the partial type.
  //
  // More notes:
  // If 'TComponentState' just only extended 'Partial<TComponent>',
  // then it would allow excess properties on the 'initialState' that don't match 'TComponent'
  // (This is intended behavior by TypeScript: https://github.com/microsoft/TypeScript/issues/31872).
  // I can't think of a reason for why it would ever be necessary to allow excess properties on the 'initialState'
  // for whatever modal component is being used here, but i guess this could be changed in the future.
  showComponentModal<
    TComponent extends AnyModalComponent,
    TComponentState extends ExactlyPartial<TComponent, TComponentState>,
  >(
    component: Type<TComponent>,
    config?: ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>,
  ): Promise<InferModalResult<TComponent>> {
    const initialChatBotState = this.cgmChatService.getHiddenState();
    this.cgmChatService.hideChatBot();
    return this.ngZoneService.runIfNecessary(() => {
      return this.showComponentModal_disregardZone(component, config);
    }).finally(() => {
      // https://github.com/angular/angular/pull/49589
      // https://github.com/angular/angular/pull/46462
      this.cgmChatService.setHiddenState(initialChatBotState);
    });
  }

  // This function has the main logic, without caring about whether it's in the right zone,
  // which is why it's wrapped in a call to NgZoneService.runIfNecessary() above.
  protected showComponentModal_disregardZone<
    TComponent extends AnyModalComponent,
    TComponentState extends ExactlyPartial<TComponent, TComponentState>,
  >(
    component: Type<TComponent>,
    config?: ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>,
  ): Promise<InferModalResult<TComponent>> {
    const resultPromise = new Promise<InferModalResult<TComponent>>((resolve, reject) => {
      if (config?.injector === this.moduleInjector) {
        // config.injector should normally be a NodeInjector from a component, to be able to inject non-singleton services in the modal.
        // Currently, it makes no sense to pass in a root module injector, as that is already the default.
        // eslint-disable-next-line max-len
        throw new Error(`Error trying to open modal '${stringifyClass(component)}'. DialogConfig.injector matches root module injector; this is probably a mistake, since that is the default`);
      }


      const activeModal = new ApActiveModal<InferModalResult<TComponent>>(resolve, reject);

      // Create a new simulated element Injector specifically for UpgradeComponents that may exist inside of this modal,
      // so that they can access the $scope properly.
      // (I'm calling it a simulated element injector, because it's not really an element injector,
      // like the one that would get injected into a component, but it does the necessary task of providing $scope)
      // And by creating a new Injector each time through here, means that the ScopeProvider will provide a new child scope
      // linked to the $rootScope for each new modal, which is what we want.
      // Then each UpgradeComponent inside of this modal will have this new scope as a parent, which is correct.
      // (as opposed to depending on the ScopeProvider being a provider for the whole module, which means a single scope gets created
      // the first time an UpgradeComponent needs one, and then is shared with all future ones after that in the same module,
      // which feels wrong)
      // The Injector that is injected into this class, and into the modal class is a module injector,
      // which doesn't provide $scope.
      const elementInjector = Injector.create({
        name: 'ApModalService-ElementInjector',
        parent: this.moduleInjector,
        providers: [
          ScopeProvider,
        ],
      });

      // ApActiveModal always needs to get filled in on the initialState for the modal component to return properly back to the caller
      const completeConfig = config ?? new ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>();
      completeConfig.elementInjector = elementInjector;
      const activeModalProv: StaticProvider = {
        provide: ApActiveModal,
        useValue: activeModal,
      };
      const _providers = completeConfig.providers || [];
      if (typeof _providers === 'function') {
        completeConfig.providers = (...args) => {
          return [
            activeModalProv,
            ..._providers(...args),
          ];
        };
      } else {
        completeConfig.providers = [
          activeModalProv,
          ..._providers,
        ];
      }
      this.makeCompatibleWithBootstrapModal(completeConfig, {
        overlay: this.overlay,
      });

      try {
        const dialogRef = this.dialog.open(component, completeConfig);
        destroyManualInjectorWhen(elementInjector, dialogRef.closed);
        this.makeCompatibleWithBootstrapModalAfterOpen(dialogRef, {
          dialog: this.dialog,
          documentElement: this.documentElement,
          overlayContainer: this.overlayContainer,
        });
      } catch (e) {
        destroyManualInjector(elementInjector);
        const messagePrefix = `Error trying to open modal '${stringifyClass(component)}'`;
        console.error(messagePrefix, e);
        activeModal.dismiss(e);
        throw e;
      }
    });

    return resultPromise;
  }

  showComponentModal$<
    TComponent extends AnyModalComponent,
    TComponentState extends ExactlyPartial<TComponent, TComponentState>,
  >(
    component: Type<TComponent>,
    config?: ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>,
  ): Observable<InferModalResult<TComponent>> {
    return from(this.showComponentModal(component, config));
  }
}
